import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  UserCircle, 
  ChevronDown, 
  Bell, 
  Search, 
  Settings, 
  LogOut, 
  Home,
  Package,
  ShoppingCart,
  Users,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  CreditCard,
  TrendingUp,
  Plus,
  RefreshCw,
  Shield,
  Activity,
  Truck,
  ExternalLink,
  Database,
  FolderPlus,
} from 'lucide-react';
import ADMIN_PATHS from '../ADMIN_PATHS';
import { logout as adminLogout } from '../../../features/adminAuthSlice';
import { logout as userLogout } from '../../../features/userAuthSlice';
import { getAdminSidebarSearchItems } from './adminNavConfig';
import API from '../../../util/API';
import {
  emitSystemDefaultCurrencyChanged,
} from '../../../util/systemCurrency';

const resolveProfilePic = (src) => {
  if (!src) return '';
  const raw = String(src);
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;

  const base = String(API?.defaults?.baseURL || '').replace(/\/$/, '');
  if (base) {
    const origin = base.endsWith('/api') ? base.slice(0, -4) : base;
    return `${origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
  }

  return `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
};

const shortenName = (value, max = 14) => {
  const str = String(value || '').trim();
  if (!str) return 'Admin';
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
};

export default function AdminHeader({ onOpenSidebar }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user: adminUser } = useSelector((state) => state.adminAuth || {});
  const [notifications, setNotifications] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCurrencySettings, setShowCurrencySettings] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState(['USD']);
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  const [savedDefaultCurrency, setSavedDefaultCurrency] = useState('USD');
  const [currencySettingsLoading, setCurrencySettingsLoading] = useState(false);
  const [currencySettingsSaving, setCurrencySettingsSaving] = useState(false);
  const [currencySettingsError, setCurrencySettingsError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeSearchIndex, setActiveSearchIndex] = useState(-1);
  const searchWrapRef = useRef(null);

  const displayName = adminUser?.username || adminUser?.name || adminUser?.email || 'Admin';
  const shortName = shortenName(displayName);
  const displayRole = adminUser?.role || adminUser?.roleId?.name || 'Admin';
  const avatarSrc = resolveProfilePic(adminUser?.profilePic);
  const lastLoginLabel = adminUser?.lastLogin || '—';

  const loadNotifications = async () => {
    try {
      const res = await API.get('/notification/admin?limit=20');
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setNotifications(list);
    } catch (e) {
      // Non-blocking: keep header usable even if API is down.
      console.warn('Failed to load notifications', e?.message || e);
    }
  };

  const loadCurrencySettings = async () => {
    setCurrencySettingsLoading(true);
    setCurrencySettingsError('');
    setCurrencyOptions(['USD']);
    setDefaultCurrency('USD');
    setSavedDefaultCurrency('USD');
    setCurrencySettingsLoading(false);
  };

  const saveDefaultCurrency = async () => {
    const nextCurrency = 'USD';
    setCurrencySettingsSaving(true);
    setCurrencySettingsError('');

    try {
      setSavedDefaultCurrency(nextCurrency);
      setDefaultCurrency(nextCurrency);
      emitSystemDefaultCurrencyChanged(nextCurrency);
    } catch (error) {
      setCurrencySettingsError(error?.response?.data?.message || error?.message || 'Failed to save default currency.');
    } finally {
      setCurrencySettingsSaving(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Refresh when opening dropdown so the panel always shows the latest.
    if (showNotifications) {
      loadNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNotifications]);

  useEffect(() => {
    if (!showCurrencySettings) return;
    loadCurrencySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCurrencySettings]);

  const allSearchItems = useMemo(() => getAdminSidebarSearchItems(), []);
  const trimmedSearch = searchQuery.trim();
  const searchResults = useMemo(() => {
    if (!trimmedSearch) return [];

    const q = trimmedSearch.toLowerCase();
    return allSearchItems
      .filter((it) => {
        const label = String(it.label || '').toLowerCase();
        const category = String(it.category || '').toLowerCase();
        const to = String(it.to || '').toLowerCase();
        return label.includes(q) || category.includes(q) || to.includes(q);
      })
      .slice(0, 12);
  }, [allSearchItems, trimmedSearch]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      const el = searchWrapRef.current;
      if (!el) return;
      if (!el.contains(e.target)) {
        setIsSearchOpen(false);
        setActiveSearchIndex(-1);
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  useEffect(() => {
    // Reset highlight whenever the result set changes.
    setActiveSearchIndex(-1);
  }, [trimmedSearch]);

  const handleSearchSelect = (item) => {
    if (!item?.to) return;

    if (item.external) {
      window.open(item.to, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.to);
    }

    setSearchQuery('');
    setIsSearchOpen(false);
    setActiveSearchIndex(-1);
  };

  const handleSearchKeyDown = (e) => {
    if (!isSearchOpen) return;

    if (e.key === 'Escape') {
      setIsSearchOpen(false);
      setActiveSearchIndex(-1);
      return;
    }

    if (!searchResults.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSearchIndex((prev) => {
        const next = prev + 1;
        return next >= searchResults.length ? 0 : next;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSearchIndex((prev) => {
        const next = prev - 1;
        return next < 0 ? searchResults.length - 1 : next;
      });
      return;
    }

    if (e.key === 'Enter') {
      if (activeSearchIndex >= 0 && activeSearchIndex < searchResults.length) {
        e.preventDefault();
        handleSearchSelect(searchResults[activeSearchIndex]);
      }
    }
  };

  // Content-aware header based on current route
  const getHeaderContent = () => {
    const path = location.pathname;
    
    if (path.includes('/dashboard')) {
      return {
        title: 'Dashboard',
        subtitle: 'Store Overview & Analytics',
        icon: <BarChart3 className="w-6 h-6 text-slate-700" />,
        quickActions: [
          { label: 'Add Product', icon: Plus, action: () => navigate(ADMIN_PATHS.PRODUCTS.ALIEXPRESS) },
          { label: 'View Orders', icon: ShoppingCart, action: () => navigate(ADMIN_PATHS.ORDERS) },
          { label: 'Analytics', icon: TrendingUp, action: () => navigate(ADMIN_PATHS.ANALYTICS) }
        ]
      };
    }
    
    if (path.includes('/products')) {
      return {
        title: 'Product Management',
        subtitle: 'Manage your product catalog',
        icon: <Package className="w-6 h-6 text-green-600" />,
        quickActions: [
          { label: 'Add Product', icon: Plus, action: () => navigate(ADMIN_PATHS.PRODUCTS.ALIEXPRESS) },
          { label: 'View All', icon: Package, action: () => navigate(ADMIN_PATHS.PRODUCTS.BASE) },
          { label: 'Create Category', icon: FolderPlus, action: () => navigate(ADMIN_PATHS.CATEGORY) }
        ]
      };
    }
    
    if (path.includes('/orders')) {
      return {
        title: 'Order Management',
        subtitle: 'Track and manage customer orders',
        icon: <ShoppingCart className="w-6 h-6 text-purple-600" />,
        quickActions: [
          { label: 'New Orders', icon: Plus, action: () => navigate(ADMIN_PATHS.ORDERS) },
          { label: 'Pending', icon: Clock, action: () => navigate(ADMIN_PATHS.ORDERS) },
          { label: 'Analytics', icon: BarChart3, action: () => navigate(ADMIN_PATHS.ANALYTICS) }
        ]
      };
    }
    
    if (path.includes('/users')) {
      return {
        title: 'User Management',
        subtitle: 'Manage customer accounts and permissions',
        icon: <Users className="w-6 h-6 text-indigo-600" />,
        quickActions: [
          { label: 'Add User', icon: Plus, action: () => navigate(ADMIN_PATHS.USERS) },
          { label: 'View All', icon: Users, action: () => navigate(ADMIN_PATHS.USERS) },
          { label: 'Analytics', icon: Activity, action: () => navigate(ADMIN_PATHS.ANALYTICS) }
        ]
      };
    }
    
    if (path.includes('/analytics')) {
      return {
        title: 'Analytics & Reports',
        subtitle: 'Business insights and performance metrics',
        icon: <TrendingUp className="w-6 h-6 text-orange-600" />,
        quickActions: [
          { label: 'Sales Report', icon: DollarSign, action: () => navigate(ADMIN_PATHS.ANALYTICS) },
          { label: 'User Analytics', icon: Users, action: () => navigate(ADMIN_PATHS.ANALYTICS) },
          { label: 'Export Data', icon: BarChart3, action: () => navigate(ADMIN_PATHS.ANALYTICS) }
        ]
      };
    }
    
    if (path.includes('/aliexpress-integration') || path.includes('/aliexpress')) {
      return {
        title: 'AliExpress Integration',
        subtitle: 'Connect and manage your AliExpress integration',
        icon: <Package className="w-6 h-6 text-slate-700" />,
        quickActions: [
          { label: 'Refresh', icon: RefreshCw, action: () => window.location.reload() },
          { label: 'API Docs', icon: ExternalLink, action: () => window.open('https://developers.aliexpress.com/en/doc.htm', '_blank') },
          { label: 'Settings', icon: Settings, action: () => navigate(ADMIN_PATHS.SETTINGS) }
        ]
      };
    }

    if (path.includes('/integrations/gateways') || path.includes('/payment-gateways')) {
      return {
        title: 'Gateways',
        subtitle: 'Configure payment gateways, icons, credentials, and availability rules',
        icon: <CreditCard className="w-6 h-6 text-emerald-600" />,
        quickActions: [
          { label: 'Refresh', icon: RefreshCw, action: () => window.location.reload() },
          { label: 'Finance', icon: DollarSign, action: () => navigate(ADMIN_PATHS.FINANCE) },
          { label: 'Settings', icon: Settings, action: () => navigate(ADMIN_PATHS.SETTINGS) }
        ]
      };
    }
    
    if (path.includes('/courier-integration') || path.includes('/shipping')) {
      return {
        title: 'Shipping',
        subtitle: 'AliExpress shipment tracking sync status and order shipping queue',
        icon: <Truck className="w-6 h-6 text-purple-600" />,
        quickActions: [
          { label: 'Refresh', icon: RefreshCw, action: () => window.location.reload() },
          { label: 'Settings', icon: Settings, action: () => navigate(ADMIN_PATHS.SETTINGS) }
        ]
      };
    }
    
    if (path.includes('/settings')) {
      return {
        title: 'System Settings',
        subtitle: 'Configure platform settings and preferences',
        icon: <Settings className="w-6 h-6 text-slate-700" />,
        quickActions: [
          { label: 'General', icon: Settings, action: () => navigate(ADMIN_PATHS.SETTINGS) },
          { label: 'Security', icon: Shield, action: () => navigate(ADMIN_PATHS.ADMIN.SECURITY) },
          { label: 'Backup', icon: RefreshCw, action: () => navigate(ADMIN_PATHS.BACKUP) }
        ]
      };
    }
    
    // Default
    return {
      title: 'Admin Panel',
      subtitle: 'Manage your e-commerce platform',
      icon: <Home className="w-6 h-6 text-slate-700" />,
      quickActions: [
        { label: 'Dashboard', icon: Home, action: () => navigate(ADMIN_PATHS.DASHBOARD) },
        { label: 'Products', icon: Package, action: () => navigate(ADMIN_PATHS.PRODUCTS.BASE) },
        { label: 'Orders', icon: ShoppingCart, action: () => navigate(ADMIN_PATHS.ORDERS) }
      ]
    };
  };

  const headerContent = getHeaderContent();
  const unreadNotifications = notifications.filter(n => !n.isRead).length;
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const isProductCatalogPage = normalizedPath === ADMIN_PATHS.PRODUCTS.BASE;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order_created':
      case 'order':
        return <ShoppingCart className="w-4 h-4 text-slate-700" />;
      case 'alert':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'system':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'user':
        return <Users className="w-4 h-4 text-purple-600" />;
      default: return <Bell className="w-4 h-4 text-slate-600" />;
    }
  };

  const markNotificationRead = async (notificationId) => {
    if (!notificationId) return;
    try {
      const res = await API.patch(`/notification/admin/${notificationId}/read`);
      const updated = res?.data?.data;
      if (!updated?._id) return;
      setNotifications((prev) =>
        prev.map((n) => (String(n._id) === String(updated._id) ? { ...n, ...updated } : n))
      );
    } catch (e) {
      console.warn('Failed to mark notification read', e?.message || e);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await API.patch('/notification/admin/read-all');
      // Local optimistic update
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (e) {
      console.warn('Failed to mark all notifications read', e?.message || e);
    }
  };

  const handleLogout = () => {
    // Clear any session tokens/state for this origin.
    dispatch(adminLogout());
    dispatch(userLogout());
    setShowUserMenu(false);
    navigate(ADMIN_PATHS.AUTH.LOGIN, { replace: true });
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white">
      <div className={isProductCatalogPage ? "px-4 py-2 md:px-6" : "px-4 md:px-6 py-3 md:py-4"}>
        {/* Top Row - Title and User */}
        <div
          className={
            isProductCatalogPage
              ? "flex items-center justify-between gap-3"
              : "flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:mb-4"
          }
        >
          {/* Page Title */}
          {isProductCatalogPage ? (
            onOpenSidebar ? (
              <button
                type="button"
                onClick={onOpenSidebar}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white hover:bg-slate-50 lg:hidden"
                aria-label="Open sidebar"
              >
                <ChevronDown className="w-5 h-5 text-slate-700 -rotate-90" />
              </button>
            ) : (
              <div className="hidden lg:block" />
            )
          ) : (
            <div className="flex min-w-0 items-start gap-3">
              {onOpenSidebar ? (
                <button
                  type="button"
                  onClick={onOpenSidebar}
                  className="lg:hidden h-10 w-10 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center"
                  aria-label="Open sidebar"
                >
                  <ChevronDown className="w-5 h-5 text-slate-700 -rotate-90" />
                </button>
              ) : null}
              <div className="rounded-md border border-slate-200 bg-white p-2">
                {headerContent.icon}
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-semibold text-slate-900">{headerContent.title}</h1>
                <p className="text-sm text-slate-600 truncate">{headerContent.subtitle}</p>
              </div>
            </div>
          )}

          {/* Right Side - Search, Notifications, User */}
          <div
            className={
              isProductCatalogPage
                ? "flex min-w-0 flex-1 items-center justify-end gap-2"
                : "flex flex-col gap-3 md:flex-row md:items-center md:gap-4 min-w-0"
            }
          >
            {/* Search */}
            <div className="relative w-full md:w-auto min-w-0" ref={searchWrapRef}>
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsSearchOpen(true);
                }}
                onFocus={() => setIsSearchOpen(true)}
                onKeyDown={handleSearchKeyDown}
                className="w-full max-w-full rounded-md border border-slate-200 py-2 pl-10 pr-4 text-sm focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10 md:w-64 lg:w-72"
              />

              {isSearchOpen && trimmedSearch && (
                <div className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg">
                  {searchResults.length ? (
                    <div className="max-h-72 overflow-y-auto">
                      {searchResults.map((item, idx) => {
                        const Icon = item.icon;
                        const isActive = idx === activeSearchIndex;
                        return (
                          <button
                            key={`${item.to}-${item.label}`}
                            type="button"
                            onMouseDown={(e) => {
                              // Prevent input blur before click handler runs.
                              e.preventDefault();
                              handleSearchSelect(item);
                            }}
                            className={`w-full px-3 py-2 text-left hover:bg-slate-50 flex items-start gap-3 ${
                              isActive ? 'bg-slate-50' : ''
                            }`}
                          >
                            <div className="mt-0.5">
                              {Icon ? <Icon className="w-4 h-4 text-slate-500" /> : null}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-900 truncate">{item.label}</p>
                              <p className="text-xs text-slate-500 truncate">{item.category}</p>
                            </div>
                            {item.external ? (
                              <ExternalLink className="w-4 h-4 text-slate-400 mt-0.5" />
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="px-3 py-3 text-sm text-slate-500">No matches</div>
                  )}
                </div>
              )}
            </div>

            {/* Notifications */}
            <div className="flex items-center gap-2">
              <div className="relative">
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  setShowCurrencySettings(false);
                  setShowUserMenu(false);
                }}
                className="relative rounded-md p-2 transition-colors hover:bg-slate-50"
              >
                <Bell className="w-5 h-5 text-slate-600" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#D2042D] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadNotifications}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 mt-2 w-[calc(100vw-1rem)] sm:w-80 bg-white border border-slate-200 rounded-md shadow-lg z-50">
                  <div className="p-4 border-b border-slate-200">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-semibold text-slate-900">Notifications</h3>
                      {unreadNotifications > 0 ? (
                        <button
                          onClick={markAllNotificationsRead}
                          className="text-xs text-slate-900 hover:text-[#D2042D]"
                          type="button"
                        >
                          Mark all as read
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.map((notification) => {
                      const id = notification?._id;
                      const isRead = Boolean(notification?.isRead);
                      const title = notification?.title || 'Notification';
                      const message = notification?.message;
                      const time = notification?.createdAt
                        ? new Date(notification.createdAt).toLocaleString()
                        : '';

                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => {
                            if (!isRead) markNotificationRead(id);
                            const link = notification?.link;
                            if (link) navigate(link);
                            setShowNotifications(false);
                          }}
                          className={`w-full text-left p-4 border-b border-slate-100 hover:bg-slate-50 ${
                            !isRead ? 'bg-slate-50' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {getNotificationIcon(notification.type)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{title}</p>
                              {message ? (
                                <p className="text-sm text-slate-700 mt-0.5 line-clamp-2">{message}</p>
                              ) : null}
                              {time ? (
                                <p className="text-xs text-slate-500 mt-1">{time}</p>
                              ) : null}
                            </div>
                            {!isRead ? <div className="w-2 h-2 bg-[#D2042D] rounded-full" /> : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="p-3 border-t border-slate-200">
                    <button
                      type="button"
                      onClick={() => {
                        setShowNotifications(false);
                        navigate(ADMIN_PATHS.NOTIFICATIONS);
                      }}
                      className="text-sm text-slate-900 hover:text-[#D2042D]"
                    >
                      View all notifications
                    </button>
                  </div>
                </div>
              )}
              </div>

              {/* System Currency Settings */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowCurrencySettings(!showCurrencySettings);
                    setShowNotifications(false);
                    setShowUserMenu(false);
                  }}
                  className="relative rounded-md p-2 transition-colors hover:bg-slate-50"
                  title="System Currency Settings"
                >
                  <Settings className="w-5 h-5 text-slate-600" />
                </button>

                {showCurrencySettings && (
                  <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 mt-2 w-[calc(100vw-1rem)] sm:w-80 bg-white border border-slate-200 rounded-md shadow-lg z-50">
                    <div className="p-4 border-b border-slate-200">
                      <h3 className="font-semibold text-slate-900">System Default Currency</h3>
                      <p className="text-xs text-slate-500 mt-1">Internal accounting base currency.</p>
                    </div>

                    <div className="p-4 space-y-3">
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
                        Currency
                      </label>
                      <select
                        value={defaultCurrency}
                        onChange={() => setDefaultCurrency('USD')}
                        disabled={currencySettingsLoading || currencySettingsSaving}
                        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                      >
                        {currencyOptions.map((code) => (
                          <option key={code} value={code}>{code}</option>
                        ))}
                      </select>

                      {currencySettingsError ? (
                        <p className="text-xs text-red-600">{currencySettingsError}</p>
                      ) : null}
                      {!currencySettingsError && savedDefaultCurrency === defaultCurrency ? (
                        <p className="text-xs text-emerald-600">Saved default: {savedDefaultCurrency}</p>
                      ) : null}
                    </div>

                    <div className="p-3 border-t border-slate-200 flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={loadCurrencySettings}
                        disabled={currencySettingsLoading || currencySettingsSaving}
                        className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-60"
                      >
                        Refresh
                      </button>
                      <button
                        type="button"
                        onClick={saveDefaultCurrency}
                        disabled={currencySettingsLoading || currencySettingsSaving}
                        className="px-3 py-2 text-sm font-semibold text-white bg-slate-900 rounded-md hover:bg-black disabled:opacity-60"
                      >
                        {currencySettingsSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

            {/* User Menu */}
              <div className="relative">
              <button
                onClick={() => {
                  setShowUserMenu(!showUserMenu);
                  setShowNotifications(false);
                  setShowCurrencySettings(false);
                }}
                className="flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-slate-50"
              >
                {avatarSrc ? (
                  <img src={avatarSrc} alt="avatar" className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                ) : (
                  <UserCircle className="w-8 h-8 text-slate-400" />
                )}
                <div className="text-left hidden sm:block">
                  <p className="text-sm font-medium text-slate-900">{shortName}</p>
                  <p className="text-xs text-slate-500">{displayRole}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>

              {/* User Dropdown */}
              {showUserMenu && (
                <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 mt-2 w-[calc(100vw-1rem)] sm:w-56 bg-white border border-slate-200 rounded-md shadow-lg z-50">
                  <div className="p-4 border-b border-slate-200">
                    <p className="text-sm font-medium text-slate-900">{displayName}</p>
                    <p className="text-xs text-slate-500">{displayRole}</p>
                    <p className="text-xs text-slate-400 mt-1">Last login: {lastLoginLabel}</p>
                  </div>
                  <div className="p-2">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate(ADMIN_PATHS.WORKUPDATE);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-2"
                    >
                      <UserCircle className="w-4 h-4" />
                      Profile
                    </button>
                    <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Settings
                    </button>
                    <button 
                      onClick={handleLogout}
                      className="w-full text-left px-3 py-2 text-sm text-[#D2042D] hover:bg-rose-50 rounded-md flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>

        {/* Bottom Row - Quick Actions */}
        {!isProductCatalogPage ? (
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {headerContent.quickActions.map((action, index) => (
              <button
                key={index}
                onClick={action.action}
                className="px-3 py-1.5 text-sm border border-slate-200 bg-white text-slate-700 rounded-md hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <action.icon className="w-4 h-4" />
                {action.label}
              </button>
            ))}
          </div>
          
          {/* System Status */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>System Online</span>
            </div>
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              <span>API: 99.9%</span>
            </div>
          </div>
        </div>
        ) : null}
      </div>
    </header>
  );
} 
